import { useEffect, useMemo, useRef, useState } from 'react';
import { $window, isElementPartiallyInViewport } from '@umu/umu-dom';
import { throttle } from '@umu/umu-util';

/**
 * 监听 element 是否在视窗中
 * @param {HTMLElement} element 要监测的元素
 * @param {boolean}     once 是否只监听一次，出现在视口后即移除监听
 * @param {HTMLElement} root 容器窗口
 * @returns {boolean} boolean
 * @author Kody
 */
export const useElementIntoView = (element: HTMLElement | null, once = false, root?: HTMLElement): boolean => {
    const [flag, setFlag] = useState<boolean>(false);
    const observeRef = useRef<IntersectionObserver | null>(null);

    const scrollCallback = useMemo(() => {
        return throttle(() => {
            if (element) {
                const activeFlag = isElementPartiallyInViewport(element!, root);
                setFlag(activeFlag);
                if (once && activeFlag) {
                    removeScrollListener();
                }
            }
        }, 100);
    }, [element, root]);

    const removeScrollListener = () => {
        (root || $window).removeEventListener('scroll', scrollCallback);
    };

    const initObserverScheme = (el: HTMLElement) => {
        const options = {
            root: root || null,
            rootMargin: '0px',
            threshold: [0, 0.1]
        };
        const callback = (entries: IntersectionObserverEntry[]) => {
            const entrie = entries[0];
            if (entrie) {
                const activeFlag = entrie.intersectionRatio > 0;
                setFlag(activeFlag);
                if (once && activeFlag && observeRef.current) {
                    observeRef.current.unobserve(el);
                }
            }
        };

        const initObserver = observeRef.current || new IntersectionObserver(callback, options);
        initObserver.observe(el);
        observeRef.current = initObserver;
    };

    useEffect(() => {
        if (!element) {
            setFlag(false);
            return;
        }
        const activeFlag = isElementPartiallyInViewport(element, root);
        setFlag(activeFlag);
        if (once && activeFlag) {
            return;
        }

        if ('IntersectionObserver' in $window) {
            if ('requestIdleCallback' in $window) {
                ($window as Window).requestIdleCallback(() => initObserverScheme(element), { timeout: 0 });
            } else {
                setTimeout(() => {
                    initObserverScheme(element);
                }, 0);
            }
        } else {
            (root || $window).addEventListener('scroll', scrollCallback);
        }

        return () => {
            if (observeRef.current) {
                observeRef.current.unobserve(element);
            } else {
                removeScrollListener();
            }
        };
    }, [element, root]);

    useEffect(() => {
        return () => {
            if (observeRef.current) {
                observeRef.current.disconnect();
                observeRef.current = null;
            } else {
                removeScrollListener();
            }
        };
    }, []);

    return flag;
};
